import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiClientError } from '@terra/api-client';
import { toast } from '@terra/ui';

import { api } from '../lib/api';
import { STOREFRONT_URL } from '../lib/appUrls';
import { useAuthStore } from '../stores/authStore';

/* ── Scoped CSS that overrides Tailwind base / @terra/ui globals ── */
const SCOPED_CSS = `
@keyframes ts-login-spin {
  to { transform: rotate(360deg); }
}

.ts-login-page,
.ts-login-page *,
.ts-login-page *::before,
.ts-login-page *::after {
  box-sizing: border-box;
  border-color: transparent;
}

.ts-login-page {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  min-height: 100vh;
  background-color: #efefef !important;
  font-family: 'Arial', 'Helvetica', sans-serif !important;
  margin: 0;
  padding: 24px 0;
}

.ts-home-btn {
  position: absolute;
  top: 20px;
  left: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #ffffff;
  color: #1f2937;
  border: 1px solid #e5e7eb !important;
  border-radius: 4px !important;
  padding: 8px 14px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none !important;
  transition: all 0.2s ease;
  font-family: 'Arial', 'Helvetica', sans-serif !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  z-index: 10;
}

.ts-home-btn:hover {
  background-color: #f9fafb;
  border-color: #d1d5db !important;
  color: #e53935;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.ts-home-btn svg {
  stroke: currentColor;
  transition: stroke 0.2s ease;
}

@media (max-width: 480px) {
  .ts-home-btn {
    top: 12px;
    left: 12px;
    padding: 6px 10px;
    font-size: 13px;
  }
}

.ts-login-card {
  width: 450px;
  min-height: 640px;
  background-color: #ffffff;
  border: 1px solid #e8e8e8 !important;
  border-radius: 0 !important;
  padding: 46px 40px 36px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: none !important;
}

.ts-login-logo {
  width: 170px;
  margin-bottom: 22px;
  display: block;
}

.ts-login-heading {
  font-size: 18px;
  font-weight: 700;
  color: #e53935;
  text-transform: uppercase;
  text-align: center;
  margin: 0 0 8px 0;
  letter-spacing: 0;
  line-height: 24px;
  font-style: normal !important;
  font-family: 'Arial', 'Helvetica', sans-serif !important;
}

.ts-login-subtext {
  font-size: 15px;
  color: #4b5563;
  text-align: center;
  margin: 0 0 28px 0;
  line-height: 20px;
  font-weight: 400;
  font-family: 'Arial', 'Helvetica', sans-serif !important;
}

.ts-login-form {
  width: 100%;
}

.ts-login-field {
  margin-bottom: 20px;
}

.ts-login-label {
  font-size: 14px;
  font-weight: 700;
  color: #111111;
  margin-bottom: 8px;
  display: block;
  line-height: 18px;
  font-family: 'Arial', 'Helvetica', sans-serif !important;
}

.ts-login-input {
  width: 100%;
  height: 40px;
  border: 1px solid #dddddd !important;
  border-radius: 0 !important;
  padding: 0 12px;
  background-color: #ffffff !important;
  font-size: 14px;
  line-height: 18px;
  outline: none !important;
  box-shadow: none !important;
  font-family: 'Arial', 'Helvetica', sans-serif !important;
  color: #111111;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}
.ts-login-input::placeholder {
  color: #9ca3af;
}
.ts-login-input:focus {
  border-color: #e53935 !important;
  outline: none !important;
  box-shadow: none !important;
}

.ts-login-select {
  width: 100%;
  height: 40px;
  border: 1px solid #dddddd !important;
  border-radius: 0 !important;
  padding: 0 12px;
  background-color: #ffffff !important;
  font-size: 14px;
  line-height: 18px;
  outline: none !important;
  box-shadow: none !important;
  font-family: 'Arial', 'Helvetica', sans-serif !important;
  color: #111111;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23111111' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
  padding-right: 32px;
  cursor: pointer;
}
.ts-login-select:focus {
  border-color: #e53935 !important;
}

.ts-login-pw-wrap {
  position: relative;
}

.ts-login-pw-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #111827;
  display: flex;
  align-items: center;
  background: none !important;
  border: none !important;
  cursor: pointer;
  padding: 0;
  font-size: 20px;
  outline: none !important;
  box-shadow: none !important;
}

.ts-login-submit {
  width: 100%;
  height: 42px;
  background-color: #e53935 !important;
  color: #ffffff !important;
  font-weight: 700;
  font-size: 15px;
  border-radius: 0 !important;
  border: none !important;
  cursor: pointer;
  margin-top: 10px;
  margin-bottom: 24px;
  transition: background-color 0.2s;
  font-family: 'Arial', 'Helvetica', sans-serif !important;
  letter-spacing: 0;
  line-height: 16px;
  text-transform: uppercase;
  outline: none !important;
  box-shadow: none !important;
  -webkit-appearance: none;
  appearance: none;
}
.ts-login-submit:hover {
  background-color: #d32f2f !important;
}
.ts-login-submit:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.ts-login-forgot {
  font-size: 14px;
  font-weight: 700;
  color: #e53935;
  text-decoration: none;
  background: none !important;
  border: none !important;
  cursor: pointer;
  padding: 0;
  font-family: 'Arial', 'Helvetica', sans-serif !important;
  outline: none !important;
}
.ts-login-forgot:hover {
  text-decoration: underline;
}

.ts-login-back {
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  text-align: center;
  display: block;
  text-decoration: none !important;
  font-family: 'Arial', 'Helvetica', sans-serif !important;
  line-height: 20px;
  cursor: pointer;
}
.ts-login-back:hover {
  color: #111111;
  text-decoration: underline !important;
}

.ts-login-error {
  font-size: 12px;
  color: #d32f2f;
  margin: 4px 0 0 0;
  line-height: 16px;
  font-family: 'Arial', 'Helvetica', sans-serif !important;
  font-weight: 600;
}

.ts-login-global-error {
  width: 100%;
  font-size: 13px;
  color: #d32f2f;
  background-color: #fef2f2;
  border: 1px solid #fee2e2;
  padding: 10px 12px;
  margin-bottom: 20px;
  line-height: 18px;
  font-family: 'Arial', 'Helvetica', sans-serif !important;
}

.ts-login-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #ffffff;
  border-radius: 50% !important;
  animation: ts-login-spin 0.6s linear infinite;
  vertical-align: middle;
  margin-right: 8px;
}

/* Steps tracker indicators */
.ts-step-indicator {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-bottom: 28px;
  width: 100%;
}
.ts-step-dot {
  width: 10px;
  height: 10px;
  background-color: #e5e7eb;
  border-radius: 50%;
  transition: background-color 0.3s;
}
.ts-step-dot.active {
  background-color: #e53935;
}
.ts-step-dot.completed {
  background-color: #e53935;
}
.ts-step-connector {
  flex: 1;
  max-width: 60px;
  height: 2px;
  background-color: #e5e7eb;
}
.ts-step-connector.active {
  background-color: #e53935;
}

/* OTP step — wide tracking so a 6-digit code stays readable at a glance. */
.ts-otp-note {
  width: 100%;
  font-size: 13px;
  color: #166534;
  background-color: #f0fdf4;
  border: 1px solid #dcfce7;
  padding: 10px 12px;
  margin-bottom: 20px;
  line-height: 18px;
  font-family: 'Arial', 'Helvetica', sans-serif !important;
}
.ts-otp-code {
  text-align: center;
  letter-spacing: 8px;
  font-size: 20px !important;
  font-weight: 700;
}
.ts-otp-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-top: 12px;
  font-size: 13px;
}
`;

const TERRA_LOGO_URL =
  'https://lh3.googleusercontent.com/aida-public/AB6AXuCSmwtT-Za5b9CHsGaDhtFOTNwsZDaQldEF8lQZ9Elh4pjiafSgFIIoEK2sZDeGepJNhatnt0A_-cCJRnD1Lgx8lN4TeB8y-NrWrDTqDHyQ8QQoyco8I5YgDKEo7fb0MVYXSXbMZ82Qks4VxtGbNijakpYmro5fgKJzAtGyw5uVHt_bz5FiuQ8ACF1dRmYGcyAWcFBespTd8qFoUqwf5GTMjMJfjpSQuvXkum-3rTBxqpDGQlT4XoidUfRPUDjo4_vnKJE7Dt2tzrI';

const BUSINESS_TYPES = [
  'Individual/Proprietorship',
  'Partnership Firm',
  'Private Limited Company',
  'Limited Liability Partnership (LLP)',
  'One Person Company (OPC)',
  'Other',
];

export function RegisterPage() {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);

  // Check if referrer is storefront
  const queryParams = new URLSearchParams(window.location.search);
  const isFromQuery = queryParams.get('from') === 'storefront';
  if (isFromQuery) {
    sessionStorage.setItem('referrer_source', 'storefront');
  }
  const showHomeButton = isFromQuery || sessionStorage.getItem('referrer_source') === 'storefront';

  // Flow State
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);

  // Form Fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [storeName, setStoreName] = useState('');
  const [businessType, setBusinessType] = useState('Individual/Proprietorship');
  const [gstin, setGstin] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');

  // Field validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Phone verification state — step 3.
  const [otpCode, setOtpCode] = useState('');
  const [otpSentTo, setOtpSentTo] = useState<string | null>(null);

  // Step 2 submit only *starts* the sign-up: it validates the store details,
  // holds them server-side and texts a code. The seller account and the Vendor
  // row are created by `verifyMutation`, so a store can never be registered
  // against an unverified mobile number.
  const registerMutation = useMutation({
    mutationFn: () =>
      api.auth.registerVendor({
        name,
        email,
        phone,
        password,
        storeName,
        businessType,
        gstin: gstin.trim() || undefined,
        city,
        state,
      }),
    onSuccess: (res) => {
      setOtpCode('');
      setOtpSentTo(res.deliveredTo);
      setStep(3);
      toast.success(`OTP sent to ${res.deliveredTo}`);
    },
    onError: (err) => {
      const msg =
        err instanceof ApiClientError
          ? err.message
          : 'An error occurred during registration. Please try again.';
      toast.error(msg);
    },
  });

  const verifyMutation = useMutation({
    mutationFn: () => api.auth.verifyRegistration({ phone, code: otpCode }),
    onSuccess: (res) => {
      toast.success('Mobile number verified — your seller account is created!');
      setSession(res);
      navigate('/', { replace: true });
    },
    onError: (err) => {
      toast.error(err instanceof ApiClientError ? err.message : 'Invalid or expired OTP');
    },
  });

  const resendMutation = useMutation({
    mutationFn: () => api.auth.resendRegistrationOtp({ phone }),
    onSuccess: (res) => {
      setOtpSentTo(res.deliveredTo);
      toast.success(`A new OTP is on its way to ${res.deliveredTo}`);
    },
    onError: (err) => {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not resend the OTP.');
    },
  });

  // Client Validation for Step 1
  const validateStep1 = () => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = 'Full name is required';
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      errs.email = 'Enter a valid email address';
    if (!phone.trim() || !/^\d{10}$/.test(phone))
      errs.phone = 'Enter a valid 10-digit mobile number';
    if (password.length < 8) errs.password = 'Password must be at least 8 characters';
    if (password !== confirmPassword) errs.confirmPassword = 'Passwords do not match';

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Client Validation for Step 2
  const validateStep2 = () => {
    const errs: Record<string, string> = {};
    if (!storeName.trim()) errs.storeName = 'Store name is required';
    if (!businessType) errs.businessType = 'Business type is required';
    if (!city.trim()) errs.city = 'City is required';
    if (!state.trim()) errs.state = 'State is required';
    if (
      gstin.trim() &&
      !/^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}$/.test(gstin.trim().toUpperCase())
    ) {
      errs.gstin = 'Enter a valid GSTIN format (optional, e.g. 27AAAAA1111A1Z1)';
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNextStep = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateStep1()) {
      setStep(2);
    }
  };

  const handleRegisterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateStep2()) {
      registerMutation.mutate();
    }
  };

  const stepError = step === 3 ? verifyMutation.error : registerMutation.error;
  const apiError =
    stepError instanceof ApiClientError
      ? stepError.message
      : stepError
        ? 'An error occurred during registration. Please try again.'
        : null;

  return (
    <>
      <style>{SCOPED_CSS}</style>

      <div className="ts-login-page">
        {showHomeButton && (
          <a href={STOREFRONT_URL} className="ts-home-btn">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
            Home
          </a>
        )}

        <main className="ts-login-card">
          {/* ── Logo ──────────────────────────────────────────── */}
          <img
            src={TERRA_LOGO_URL}
            alt="Terra Shoppy Logo"
            className="ts-login-logo"
            draggable={false}
          />

          {/* ── Heading ───────────────────────────────────────── */}
          <h1 className="ts-login-heading">
            {step === 3 ? 'Verify Your Mobile Number' : 'Register as a Seller'}
          </h1>
          <p className="ts-login-subtext">
            {step === 3
              ? 'Enter the 6-digit code we texted you to finish registering.'
              : 'Set up your vendor store and start selling.'}
          </p>

          {/* ── Step Indicators ───────────────────────────────── */}
          <div className="ts-step-indicator">
            <div className={`ts-step-dot ${step >= 1 ? 'active' : ''}`} />
            <div className={`ts-step-connector ${step >= 2 ? 'active' : ''}`} />
            <div className={`ts-step-dot ${step >= 2 ? 'active' : ''}`} />
            <div className={`ts-step-connector ${step >= 3 ? 'active' : ''}`} />
            <div className={`ts-step-dot ${step >= 3 ? 'active' : ''}`} />
          </div>

          {/* ── Global API Error ─────────────────────────────── */}
          {apiError && <div className="ts-login-global-error">{apiError}</div>}

          {/* ── Form — Step 1: User Account ──────────────────── */}
          {step === 1 && (
            <form className="ts-login-form" onSubmit={handleNextStep}>
              {/* Full Name */}
              <div className="ts-login-field">
                <label htmlFor="reg-name" className="ts-login-label">
                  Full Name
                </label>
                <input
                  id="reg-name"
                  className="ts-login-input"
                  type="text"
                  placeholder="Enter your full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
                {errors.name && <p className="ts-login-error">{errors.name}</p>}
              </div>

              {/* Email */}
              <div className="ts-login-field">
                <label htmlFor="reg-email" className="ts-login-label">
                  Email Address
                </label>
                <input
                  id="reg-email"
                  className="ts-login-input"
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                {errors.email && <p className="ts-login-error">{errors.email}</p>}
              </div>

              {/* Mobile Number */}
              <div className="ts-login-field">
                <label htmlFor="reg-phone" className="ts-login-label">
                  Mobile Number
                </label>
                <input
                  id="reg-phone"
                  className="ts-login-input"
                  type="tel"
                  placeholder="10-digit mobile number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                />
                {errors.phone && <p className="ts-login-error">{errors.phone}</p>}
              </div>

              {/* Password */}
              <div className="ts-login-field">
                <label htmlFor="reg-password" className="ts-login-label">
                  Password
                </label>
                <div className="ts-login-pw-wrap">
                  <input
                    id="reg-password"
                    className="ts-login-input"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create a strong password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="ts-login-pw-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && <p className="ts-login-error">{errors.password}</p>}
              </div>

              {/* Confirm Password */}
              <div className="ts-login-field">
                <label htmlFor="reg-confirm-password" className="ts-login-label">
                  Confirm Password
                </label>
                <input
                  id="reg-confirm-password"
                  className="ts-login-input"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Re-enter your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
                {errors.confirmPassword && (
                  <p className="ts-login-error">{errors.confirmPassword}</p>
                )}
              </div>

              {/* Continue Button */}
              <button type="submit" className="ts-login-submit">
                Continue to Store Details
              </button>
            </form>
          )}

          {/* ── Form — Step 2: Vendor Store Details ────────── */}
          {step === 2 && (
            <form className="ts-login-form" onSubmit={handleRegisterSubmit}>
              {/* Store Name */}
              <div className="ts-login-field">
                <label htmlFor="reg-storename" className="ts-login-label">
                  Store Name
                </label>
                <input
                  id="reg-storename"
                  className="ts-login-input"
                  type="text"
                  placeholder="e.g. Grandma's Spices"
                  value={storeName}
                  onChange={(e) => setStoreName(e.target.value)}
                  required
                />
                {errors.storeName && <p className="ts-login-error">{errors.storeName}</p>}
              </div>

              {/* Business Type */}
              <div className="ts-login-field">
                <label htmlFor="reg-businesstype" className="ts-login-label">
                  Business Type
                </label>
                <select
                  id="reg-businesstype"
                  className="ts-login-select"
                  value={businessType}
                  onChange={(e) => setBusinessType(e.target.value)}
                  required
                >
                  {BUSINESS_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                {errors.businessType && <p className="ts-login-error">{errors.businessType}</p>}
              </div>

              {/* GST Number */}
              <div className="ts-login-field">
                <label htmlFor="reg-gstin" className="ts-login-label">
                  GST Number (Optional)
                </label>
                <input
                  id="reg-gstin"
                  className="ts-login-input"
                  type="text"
                  placeholder="15-digit GSTIN (optional)"
                  value={gstin}
                  onChange={(e) => setGstin(e.target.value)}
                />
                {errors.gstin && <p className="ts-login-error">{errors.gstin}</p>}
              </div>

              {/* City */}
              <div className="ts-login-field">
                <label htmlFor="reg-city" className="ts-login-label">
                  City
                </label>
                <input
                  id="reg-city"
                  className="ts-login-input"
                  type="text"
                  placeholder="Enter city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  required
                />
                {errors.city && <p className="ts-login-error">{errors.city}</p>}
              </div>

              {/* State */}
              <div className="ts-login-field">
                <label htmlFor="reg-state" className="ts-login-label">
                  State
                </label>
                <input
                  id="reg-state"
                  className="ts-login-input"
                  type="text"
                  placeholder="Enter state"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  required
                />
                {errors.state && <p className="ts-login-error">{errors.state}</p>}
              </div>

              {/* Back Button */}
              <button
                type="button"
                className="ts-login-back"
                style={{ marginBottom: '14px', width: '100%', border: 'none', background: 'none' }}
                onClick={() => setStep(1)}
              >
                ← Back to Account Details
              </button>

              {/* Submit Button */}
              <button
                type="submit"
                className="ts-login-submit"
                disabled={registerMutation.isPending}
              >
                {registerMutation.isPending && <span className="ts-login-spinner" />}
                SEND OTP &amp; CONTINUE
              </button>
            </form>
          )}

          {/* ── Form — Step 3: Mobile verification ──────────── */}
          {/* This submit is what creates the seller account and the store. */}
          {step === 3 && (
            <form
              className="ts-login-form"
              onSubmit={(e) => {
                e.preventDefault();
                verifyMutation.mutate();
              }}
            >
              <div className="ts-otp-note">
                A 6-digit code was sent to <strong>{otpSentTo}</strong>. It expires in 10 minutes.
              </div>

              <div className="ts-login-field">
                <label htmlFor="reg-otp" className="ts-login-label">
                  6-Digit OTP
                </label>
                <input
                  id="reg-otp"
                  className="ts-login-input ts-otp-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="000000"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                  required
                />
              </div>

              <button
                type="submit"
                className="ts-login-submit"
                disabled={verifyMutation.isPending || otpCode.length !== 6}
              >
                {verifyMutation.isPending && <span className="ts-login-spinner" />}
                VERIFY &amp; CREATE ACCOUNT
              </button>

              <div className="ts-otp-actions">
                <button
                  type="button"
                  className="ts-login-forgot"
                  onClick={() => resendMutation.mutate()}
                  disabled={resendMutation.isPending}
                >
                  Resend OTP
                </button>
                <button
                  type="button"
                  className="ts-login-forgot"
                  onClick={() => {
                    setStep(1);
                    setOtpCode('');
                  }}
                >
                  Change mobile number
                </button>
              </div>
            </form>
          )}

          {/* Login Redirection Link */}
          <div
            style={{
              marginTop: '4px',
              fontSize: '15px',
              fontFamily: 'Arial, Helvetica, sans-serif',
            }}
          >
            <span style={{ color: '#6b7280' }}>Already have an account? </span>
            <button type="button" className="ts-login-forgot" onClick={() => navigate('/login')}>
              Login
            </button>
          </div>
        </main>
      </div>
    </>
  );
}
